import { markRaw } from 'vue'
import JobTitleCompanyColumn from './JobTitleCompanyColumn.vue'
import JobLocationPreferenceColumn from './JobLocationPreferenceColumn.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import {
  JobCreatedAtColumnFragment,
  JobCreatedAtColumnFragmentDoc,
  JobHireAndPoNumberColumnFragment,
  JobHireAndPoNumberColumnFragmentDoc,
  JobSharedChannelsColumnFragment,
  JobSharedChannelsColumnFragmentDoc,
  JobStartDateColumnFragment,
  JobStartDateColumnFragmentDoc,
  JobEndDateColumnFragment,
  JobEndDateColumnFragmentDoc,
  JobCandidatesBidsHiresColumnFragment,
  JobCandidatesBidsHiresColumnFragmentDoc,
  JobLastCandidateSubmittedColumnFragment,
  JobLastCandidateSubmittedColumnFragmentDoc,
  JobOwnersColumnFragment,
  JobOwnersColumnFragmentDoc,
  RateColumnJobFragmentDoc,
  RateColumnJobFragment,
  SortOrder,
  JobOrderByColumn,
  JobStatusColumnFragmentDoc,
  JobStatusColumnFragment,
  JobTitleColumnFragmentDoc,
  JobTitleColumnFragment,
  JobLocationPreferenceColumnFragmentDoc,
  JobLocationPreferenceColumnFragment,
  LocationPreference,
  JobTitleCompanyColumnFragmentDoc,
  JobTitleCompanyColumnFragment,
} from '@/gql/graphql'
import JobPoNumberColumn from '@/components/GraphqlDataTable/columns/Job/JobHirePoNumberColumn.vue'
import JobTitleColumn from '@/components/GraphqlDataTable/columns/Job/JobTitleColumn.vue'
import { ColumnMaps } from '@/components/GraphqlDataTable/columns/types'
import CreatedAtColumn from '@/components/GraphqlDataTable/columns/CreatedAtColumn.vue'
import RateColumn from '@/components/GraphqlDataTable/columns/RateColumn.vue'
import JobSharedChannelsColumn from '@/components/GraphqlDataTable/columns/Job/JobSharedChannelsColumn.vue'
import JobStartDateColumn from '@/components/GraphqlDataTable/columns/Job/JobStartDateColumn.vue'
import JobEndDateColumn from '@/components/GraphqlDataTable/columns/Job/JobEndDateColumn.vue'
import JobCandidatesBidsHiresColumn from '@/components/GraphqlDataTable/columns/Job/JobCandidatesBidsHiresColumn.vue'
import JobLastCandidateSubmittedColumn from '@/components/GraphqlDataTable/columns/Job/JobLastCandidateSubmittedColumn.vue'
import JobOwnersColumn from '@/components/GraphqlDataTable/columns/Job/JobOwnersColumn.vue'
import JobStatusColumn from '@/components/GraphqlDataTable/columns/Job/JobStatusColumn.vue'

const columns: ColumnMaps = {
  JobTitleColumn: {
    fragment: JobTitleColumnFragmentDoc,
    component: markRaw(JobTitleColumn),
    columnDef: {
      title: $t('Job'),
      key: 'job.id',
      renderUsing: {
        component: markRaw(JobTitleColumn),
        componentProps: (row) => ({ row }),
      },
      ignoreClickForRowSelection: true,
    },
    dataDescriber: [
      {
        title: 'Job title',
        extraction: (row: JobTitleColumnFragment) => row.name ?? '',
      },
    ],
  },
  JobTitleCompanyColumn: {
    fragment: JobTitleCompanyColumnFragmentDoc,
    component: markRaw(JobTitleCompanyColumn),
    columnDef: {
      title: $t('Role | Company'),
      key: 'job.id',
      colClass: { 'tw-max-w-[400px]': true },
      renderUsing: {
        component: markRaw(JobTitleCompanyColumn),
        componentProps: (row) => ({ row }),
      },
      ignoreClickForRowSelection: true,
    },
    dataDescriber: [
      {
        title: 'Job title',
        extraction: (row: JobTitleCompanyColumnFragment) => row.name ?? '',
      },
      {
        title: 'Company',
        extraction: (row: JobTitleCompanyColumnFragment) => row.company?.name ?? '',
      },
    ],
  },
  JobHireAndPoNumberColumn: {
    fragment: JobHireAndPoNumberColumnFragmentDoc,
    component: markRaw(JobPoNumberColumn),
    columnDef: {
      title: $t('Job title and PO number'),
      key: 'job_title_url',
      renderUsing: {
        component: markRaw(JobPoNumberColumn),
        componentProps: (row) => ({ row }),
      },
      ignoreClickForRowSelection: true, // We have a custom click event so we want that to fire instead of row select
    },
    dataDescriber: [
      {
        title: 'Job title',
        extraction: (row: JobHireAndPoNumberColumnFragment) => row.name,
      },
      {
        title: 'Job url',
        extraction: (row: JobHireAndPoNumberColumnFragment) =>
          `${window.location.origin}${laravelNamedRoute(namedRoutes.jobs.show, { job: row.id })}`,
      },
    ],
  },
  JobCreatedAtColumn: {
    fragment: JobCreatedAtColumnFragmentDoc,
    component: markRaw(CreatedAtColumn),
    columnDef: {
      title: $t('Created'),
      key: 'job_created_at',
      orderAble: true,
      renderUsing: {
        component: markRaw(CreatedAtColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Created at',
        extraction: (row: JobCreatedAtColumnFragment) => row.createdAt,
      },
    ],
    sort: [
      {
        name: `${JobOrderByColumn.CreatedAt}_${SortOrder.Desc}`,
        orderByColumn: JobOrderByColumn.CreatedAt,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Created at'),
        secondaryText: $t('New to old'),
        title: $t('Order by the create at date'),
      },
      {
        name: `${JobOrderByColumn.CreatedAt}_${SortOrder.Asc}`,
        orderByColumn: JobOrderByColumn.CreatedAt,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Created at'),
        secondaryText: $t('Old to new'),
        title: $t('Order by the create at date'),
      },
    ],
  },
  JobStartDateColumn: {
    fragment: JobStartDateColumnFragmentDoc,
    component: markRaw(JobStartDateColumn),
    columnDef: {
      title: $t('Expected Start Date'),
      orderAble: true,
      key: 'start_date',
      renderUsing: {
        component: markRaw(JobStartDateColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Start date',
        extraction: (row: JobStartDateColumnFragment) => row.startDate,
      },
      {
        title: 'Start date timeframe',
        extraction: (row: JobStartDateColumnFragment) => row.startDateTimeframe,
      },
    ],
    sort: [
      {
        name: `${JobOrderByColumn.ExpectedStartDate}_${SortOrder.Desc}`,
        orderByColumn: JobOrderByColumn.ExpectedStartDate,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Start date'),
        secondaryText: $t('New to old'),
        title: $t('Order by the start date'),
      },
      {
        name: `${JobOrderByColumn.ExpectedStartDate}_${SortOrder.Asc}`,
        orderByColumn: JobOrderByColumn.ExpectedStartDate,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Start date'),
        secondaryText: $t('Old to new'),
        title: $t('Order by the start date'),
      },
    ],
  },
  JobEndDateColumn: {
    fragment: JobEndDateColumnFragmentDoc,
    component: markRaw(JobEndDateColumn),
    columnDef: {
      title: $t('Duration'),
      key: 'end_date',
      renderUsing: {
        component: markRaw(JobEndDateColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'End date',
        extraction: (row: JobEndDateColumnFragment) => row.endDate,
      },
      {
        title: 'End date timeframe',
        extraction: (row: JobEndDateColumnFragment) => row.endDateTimeframe,
      },
    ],
  },
  JobSharedChannelsColumn: {
    fragment: JobSharedChannelsColumnFragmentDoc,
    component: markRaw(JobSharedChannelsColumn),
    columnDef: {
      title: $t(''),
      key: 'publishedAt',
      renderUsing: {
        component: markRaw(JobSharedChannelsColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Published at',
        extraction: (row: JobSharedChannelsColumnFragment) => row.publishedAt ?? '',
      },
      {
        title: 'Published for marketplace',
        extraction: (row: JobSharedChannelsColumnFragment) => row.isForMarketplace,
      },
      {
        title: 'Published for vendors',
        extraction: (row: JobSharedChannelsColumnFragment) => row.isForRecruiters,
      },
      {
        title: 'Published for trusted contacts',
        extraction: (row: JobSharedChannelsColumnFragment) => row.isForContacts,
      },
    ],
    sort: [
      {
        name: `${JobOrderByColumn.PublishedDate}_${SortOrder.Desc}`,
        orderByColumn: JobOrderByColumn.PublishedDate,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Published date'),
        secondaryText: $t('New to old'),
        title: $t('Order by the start date'),
      },
      {
        name: `${JobOrderByColumn.PublishedDate}_${SortOrder.Asc}`,
        orderByColumn: JobOrderByColumn.PublishedDate,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Published date'),
        secondaryText: $t('Old to new'),
        title: $t('Order by the start date'),
      },
    ],
  },
  JobCandidatesBidsHiresColumn: {
    fragment: JobCandidatesBidsHiresColumnFragmentDoc,
    component: markRaw(JobCandidatesBidsHiresColumn),
    columnDef: {
      title: $t('Candidates | Hires'),
      orderAble: true,
      key: 'candidates',
      renderUsing: {
        component: markRaw(JobCandidatesBidsHiresColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Number of candidates',
        extraction: (row: JobCandidatesBidsHiresColumnFragment) =>
          `${row.workerCandidates.paginatorInfo.total}`,
      },
      {
        title: 'Number of hires',
        extraction: (row: JobCandidatesBidsHiresColumnFragment) =>
          `${row.hires.paginatorInfo.total}`,
      },
      {
        title: 'Number of required workers',
        extraction: (row: JobCandidatesBidsHiresColumnFragment) => `${row.requiredWorkers ?? '0'}`,
      },
    ],
    sort: [
      {
        name: `${JobOrderByColumn.Candidates}_${SortOrder.Desc}`,
        orderByColumn: JobOrderByColumn.Candidates,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Number of candidates'),
        secondaryText: $t('More to less'),
        title: $t('Order by number of candidates'),
      },
      {
        name: `${JobOrderByColumn.Candidates}_${SortOrder.Asc}`,
        orderByColumn: JobOrderByColumn.Candidates,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Number of candidates'),
        secondaryText: $t('Less to more'),
        title: $t('Order by number of candidates'),
      },
    ],
  },
  JobLastCandidateSubmittedColumn: {
    fragment: JobLastCandidateSubmittedColumnFragmentDoc,
    component: markRaw(JobLastCandidateSubmittedColumn),
    columnDef: {
      title: $t('Last candidate submitted'),
      key: 'lastCandidateSubmittedAt',
      orderAble: true,
      renderUsing: {
        component: markRaw(JobLastCandidateSubmittedColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Last candidate submitted',
        extraction: (row: JobLastCandidateSubmittedColumnFragment) =>
          row.lastCandidateSubmittedAt ?? '-',
      },
    ],
    sort: [
      {
        name: `${JobOrderByColumn.LastCandidateSubmittedAt}_${SortOrder.Desc}`,
        orderByColumn: JobOrderByColumn.LastCandidateSubmittedAt,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Last candidate submitted'),
        secondaryText: $t('New to old'),
        title: $t('Order by the last candidate submission date'),
      },
      {
        name: `${JobOrderByColumn.LastCandidateSubmittedAt}_${SortOrder.Asc}`,
        orderByColumn: JobOrderByColumn.LastCandidateSubmittedAt,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Last candidate submitted'),
        secondaryText: $t('Old to new'),
        title: $t('Order by the last candidate submission date'),
      },
    ],
  },
  JobOwnersColumn: {
    fragment: JobOwnersColumnFragmentDoc,
    component: markRaw(JobOwnersColumn),
    columnDef: {
      title: $t('Job owner(s)'),
      key: 'job_owners',
      renderUsing: {
        component: markRaw(JobOwnersColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Job owner(s)',
        extraction: (row: JobOwnersColumnFragment) =>
          row.owners.reduce((acc, owner) => `${acc} ${owner.name} (${owner.id})`, ''),
      },
    ],
  },
  RateColumnJob: {
    fragment: RateColumnJobFragmentDoc,
    component: markRaw(RateColumn),
    columnDef: {
      title: $t('Rate'),
      key: 'rate',
      colClass: { 'tw-min-w-[120px]': true },
      renderUsing: {
        component: markRaw(RateColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Rate',
        extraction: (row: RateColumnJobFragment) => `${row.rate}`,
      },
      {
        title: 'Rate type',
        extraction: (row: RateColumnJobFragment) => `${row.rateType}`,
      },
      {
        title: 'Currency',
        extraction: (row: RateColumnJobFragment) => `${row.currency}`,
      },
    ],
  },
  JobStatusColumn: {
    fragment: JobStatusColumnFragmentDoc,
    component: markRaw(JobStatusColumn),
    columnDef: {
      title: $t('Status'),
      key: 'status',
      renderUsing: {
        component: markRaw(JobStatusColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Status',
        extraction: (row: JobStatusColumnFragment) => `${row.status}`,
      },
    ],
  },
  JobLocationPreferenceColumn: {
    fragment: JobLocationPreferenceColumnFragmentDoc,
    component: markRaw(JobLocationPreferenceColumn),
    columnDef: {
      title: $t('Location'),
      key: 'location_preference',
      renderUsing: {
        component: markRaw(JobLocationPreferenceColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Location preference',
        extraction: (row: JobLocationPreferenceColumnFragment) => {
          switch (row.locationPreference) {
            case LocationPreference.OnsiteOnly:
              return 'On-site'
            case LocationPreference.OnsiteSome:
              return 'Hybrid'
            case LocationPreference.RemoteOnly:
              return 'Remote'
            default:
              return ''
          }
        },
      },
    ],
  },
}
export default columns
